"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { Permissions } from "@/lib/auth/roles";
import type { TicketRecord } from "@/lib/types";

export function VerifyModal({
  open,
  onOpenChange,
  onConfirm,
  onSendOtp,
  mobileHint,
  demoOtp,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (input: { method: "otp" | "pin" | "id"; code?: string; pin?: string }) => Promise<void> | void;
  onSendOtp: () => Promise<{ hint: string }>;
  mobileHint?: string;
  demoOtp?: boolean;
}) {
  const [method, setMethod] = useState<"otp" | "pin" | "id">("otp");
  const [code, setCode] = useState("");
  const [pin, setPin] = useState("");
  const [otpHint, setOtpHint] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const otpDetail = otpHint
    ? `Send a 6-digit code to the mobile number on file ${otpHint}.`
    : mobileHint
      ? `Send a 6-digit code to the mobile number on file ending ${mobileHint}.`
      : "Send a 6-digit code to the mobile number on file.";

  async function sendOtp() {
    setBusy(true);
    setError(null);
    try {
      const result = await onSendOtp();
      setOtpHint(result.hint);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to send a passcode.");
    } finally {
      setBusy(false);
    }
  }

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      await onConfirm({ method, code: code.trim() || undefined, pin: pin.trim() || undefined });
      setCode("");
      setPin("");
      setOtpHint(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Verification failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Verify customer identity</DialogTitle>
          <DialogDescription>
            Confirm who you&apos;re speaking with before sharing protected account information (CPNI).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {[
            { id: "otp" as const, title: "Text a one-time passcode", detail: otpDetail },
            { id: "pin" as const, title: "Account PIN", detail: "Customer provides their account security PIN." },
            { id: "id" as const, title: "Photo ID (in store)", detail: "Check a valid government photo ID at the counter." },
          ].map((opt) => (
            <label key={opt.id} className="flex cursor-pointer gap-3 rounded-md border border-border p-3">
              <input
                type="radio"
                name="verify-method"
                checked={method === opt.id}
                onChange={() => setMethod(opt.id)}
                className="mt-1"
              />
              <span>
                <span className="block text-sm font-semibold">{opt.title}</span>
                <span className="text-xs text-muted-foreground">{opt.detail}</span>
              </span>
            </label>
          ))}
          {method === "otp" && (
            <div className="space-y-2 rounded-md border border-border p-3">
              <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => void sendOtp()}>
                Text a one-time passcode
              </Button>
              <Label htmlFor="otp-code">6-digit code</Label>
              <Input
                id="otp-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
              />
              {demoOtp && (
                <p className="text-xs text-muted-foreground">In this demo the passcode is 000000. No SMS is sent.</p>
              )}
            </div>
          )}
          {method === "pin" && (
            <div className="space-y-2 rounded-md border border-border p-3">
              <Label htmlFor="account-pin">Account PIN</Label>
              <Input
                id="account-pin"
                type="password"
                autoComplete="off"
                value={pin}
                onChange={(event) => setPin(event.target.value)}
              />
            </div>
          )}
          {error && <p className="text-xs text-warn">{error}</p>}
          <p className="text-xs text-warn">
            Do not share call detail, usage, or full contact information until verification succeeds. Failed attempts are journaled.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={busy} onClick={() => void confirm()}>Confirm verification</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AdjustModal({
  open,
  onOpenChange,
  perms,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  perms: Permissions;
  onSubmit: (input: { amount: number; reason: string; note: string }) => void;
}) {
  const [reason, setReason] = useState("Service outage - node CHI-114");
  const [amount, setAmount] = useState("15.00");
  const [note, setNote] = useState("");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Apply a credit</DialogTitle>
          <DialogDescription>Credits post to the next invoice and are journaled with reason codes.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="adjReason">Reason</Label>
            <select id="adjReason" className="h-8 w-full rounded-md border border-input bg-card px-2" value={reason} onChange={(e) => setReason(e.target.value)}>
              <option>Service outage - node CHI-114</option>
              <option>Goodwill</option>
              <option>Billing error</option>
              <option>Fee waiver</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="adjAmount">Amount (USD)</Label>
            <Input id="adjAmount" type="number" min={1} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
            <p className="text-xs text-muted-foreground">
              Your limit as this role: ${perms.adjustLimit}. Higher amounts route to Supervisor approval.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="adjNote">Note for the record</Label>
            <Textarea id="adjNote" rows={2} placeholder="What the customer experienced and why this credit applies" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSubmit({ amount: Number(amount), reason, note })}>Apply credit</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TicketModal({
  open,
  onOpenChange,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (input: { category: string; priority: TicketRecord["priority"]; summary: string }) => void;
}) {
  const [category, setCategory] = useState("Billing");
  const [priority, setPriority] = useState<TicketRecord["priority"]>("P3");
  const [summary, setSummary] = useState("Billing inquiry - Sep roaming charges review");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create BMC Helix ticket</DialogTitle>
          <DialogDescription>Linked to this customer and this interaction automatically.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="tkCat">Category</Label>
            <select id="tkCat" className="h-8 w-full rounded-md border border-input bg-card px-2" value={category} onChange={(e) => setCategory(e.target.value)}>
              <option>Billing</option>
              <option>Network / service</option>
              <option>Device</option>
              <option>Account</option>
              <option>Order</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="tkPri">Priority</Label>
            <select
              id="tkPri"
              className="h-8 w-full rounded-md border border-input bg-card px-2"
              value={priority}
              onChange={(e) => setPriority(e.target.value as TicketRecord["priority"])}
            >
              <option value="P3">P3 - standard</option>
              <option value="P2">P2 - degraded service</option>
              <option value="P1">P1 - service down</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="tkSum">Summary</Label>
            <Input id="tkSum" value={summary} onChange={(e) => setSummary(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSubmit({ category, priority, summary })}>Create ticket</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
